import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  TextField,
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Paper,
  Typography,
  CircularProgress,
} from "@mui/material";
import { useJsApiLoader } from "@react-google-maps/api";

// Unified result model for both Pelias and Google responses
interface UnifiedAddressSuggestion {
  id: string;
  description: string;
  mainText: string;
  secondaryText?: string;
  coordinates?: { lat: number; lng: number };
  source: "pelias" | "google";
  confidence?: number;
}

// Cache interface for Google responses
interface CachedGoogleResponse {
  query: string;
  results: UnifiedAddressSuggestion[];
  timestamp: number;
  expiresAt: number;
}

interface GoogleMapsAutocompleteProps {
  value: string;
  onChange: (value: string) => void;
  onSelect: (
    address: string,
    coordinates?: { lat: number; lng: number },
  ) => void;
  placeholder?: string;
  label?: string;
  disabled?: boolean;
  fullWidth?: boolean;
  size?: "small" | "medium";
  sx?: any;
  inputProps?: any;
}

const GoogleMapsAutocomplete: React.FC<GoogleMapsAutocompleteProps> = ({
  value,
  onChange,
  onSelect,
  placeholder = "Enter address...",
  label,
  disabled = false,
  fullWidth = true,
  size = "small",
  sx,
  inputProps,
}) => {
  const [predictions, setPredictions] = useState<UnifiedAddressSuggestion[]>(
    [],
  );
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);

  const autocompleteService =
    useRef<google.maps.places.AutocompleteService | null>(null);
  const placesService = useRef<google.maps.places.PlacesService | null>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const suggestionsRef = useRef<HTMLDivElement>(null);

  // Cache for Google responses (24 hour expiry)
  const cache = useRef<Map<string, CachedGoogleResponse>>(new Map());
  const CACHE_DURATION = 24 * 60 * 60 * 1000; // 24 hours

  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: "AIzaSyDipPUXM5YzthCmrSesDjYPvc6-LmfBByk",
    libraries: ["places"],
  });

  // Initialize services when Google Maps is loaded
  useEffect(() => {
    if (isLoaded && window.google) {
      autocompleteService.current =
        new google.maps.places.AutocompleteService();

      // Create a dummy div for PlacesService (required by Google Maps API)
      const dummyDiv = document.createElement("div");
      placesService.current = new google.maps.places.PlacesService(dummyDiv);
    }
  }, [isLoaded]);

  // Pelias API function
  const searchPelias = async (
    input: string,
  ): Promise<UnifiedAddressSuggestion[]> => {
    try {
      const response = await fetch(
        `https://api.geocode.earth/v1/autocomplete?api_key=ge-fa66a0b29aae05a6&text=${encodeURIComponent(
          input,
        )}&size=5&focus.point.lat=39.8283&focus.point.lon=-98.5795&boundary.country=US,CA,MX`,
      );

      if (!response.ok) {
        throw new Error(`Pelias API error: ${response.status}`);
      }

      const data = await response.json();

      return (
        data.features?.map((feature: any, index: number) => ({
          id: `pelias_${feature.properties.gid || index}`,
          description: feature.properties.label || feature.properties.name,
          mainText: feature.properties.name || feature.properties.label,
          secondaryText: feature.properties.locality
            ? `${feature.properties.locality}, ${feature.properties.region_a}`
            : feature.properties.region_a,
          coordinates: {
            lat: feature.geometry.coordinates[1],
            lng: feature.geometry.coordinates[0],
          },
          source: "pelias" as const,
          confidence: feature.properties.confidence || 0,
        })) || []
      );
    } catch (error) {
      console.error("Pelias API error:", error);
      return [];
    }
  };

  // Google Places API function with caching
  const searchGooglePlaces = async (
    input: string,
  ): Promise<UnifiedAddressSuggestion[]> => {
    return new Promise((resolve) => {
      if (!autocompleteService.current) {
        resolve([]);
        return;
      }

      autocompleteService.current.getPlacePredictions(
        {
          input: input.trim(),
          types: ["address"],
          componentRestrictions: {
            country: ["us", "ca", "mx"],
          },
        },
        (predictions, status) => {
          if (
            status === google.maps.places.PlacesServiceStatus.OK &&
            predictions
          ) {
            const unifiedResults = predictions
              .slice(0, 5)
              .map((prediction, index) => ({
                id: `google_${prediction.place_id}`,
                description: prediction.description,
                mainText:
                  prediction.structured_formatting?.main_text ||
                  prediction.description,
                secondaryText: prediction.structured_formatting?.secondary_text,
                source: "google" as const,
                placeId: prediction.place_id,
              }));

            // Cache the results
            const cacheKey = input.trim().toLowerCase();
            cache.current.set(cacheKey, {
              query: input.trim(),
              results: unifiedResults,
              timestamp: Date.now(),
              expiresAt: Date.now() + CACHE_DURATION,
            });

            resolve(unifiedResults);
          } else {
            resolve([]);
          }
        },
      );
    });
  };

  // Main search function that tries Pelias first, then Google
  const searchPredictions = useCallback(
    async (input: string) => {
      if (!input.trim()) {
        setPredictions([]);
        setShowSuggestions(false);
        return;
      }

      setLoading(true);

      try {
        // First, try Pelias
        const peliasResults = await searchPelias(input.trim());

        // Check if Pelias returned good results (confidence > 0.5 or at least 2 results)
        const hasGoodPeliasResults =
          peliasResults.length >= 2 ||
          peliasResults.some((result) => (result.confidence || 0) > 0.5);

        let allResults = peliasResults;

        // If Pelias results are poor, try Google Places API
        if (!hasGoodPeliasResults && isLoaded) {
          // Check cache first
          const cacheKey = input.trim().toLowerCase();
          const cached = cache.current.get(cacheKey);

          if (cached && cached.expiresAt > Date.now()) {
            //console.log("Using cached Google results");
            allResults = [...peliasResults, ...cached.results];
          } else {
            //console.log("Fetching fresh Google results");
            const googleResults = await searchGooglePlaces(input.trim());
            allResults = [...peliasResults, ...googleResults];
          }
        }

        setPredictions(allResults.slice(0, 5)); // Limit to 5 total suggestions
        setShowSuggestions(true);
        setSelectedIndex(-1);
      } catch (error) {
        console.error("Search error:", error);
        setPredictions([]);
        setShowSuggestions(false);
      } finally {
        setLoading(false);
      }
    },
    [isLoaded],
  );

  // Handle input change with debouncing
  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = event.target.value;
    onChange(inputValue);

    // Clear previous timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Set new timeout for debounced search
    timeoutRef.current = setTimeout(() => {
      searchPredictions(inputValue);
    }, 300);
  };

  // Handle suggestion selection
  const handleSuggestionSelect = useCallback(
    async (suggestion: UnifiedAddressSuggestion) => {
      //console.log("handleSuggestionSelect called with:", suggestion);

      // Close suggestions immediately
      setShowSuggestions(false);
      setSelectedIndex(-1);

      if (suggestion.source === "pelias") {
        // For Pelias results, we already have coordinates
        const address = suggestion.description;
        const coordinates = suggestion.coordinates;

        onChange(address);
        onSelect(address, coordinates);
        setPredictions([]);
      } else if (suggestion.source === "google" && placesService.current) {
        // For Google results, get place details
        const placeId = (suggestion as any).placeId;
        if (!placeId) {
          // Fallback to description if no placeId
          onChange(suggestion.description);
          onSelect(suggestion.description, suggestion.coordinates);
          setPredictions([]);
          return;
        }

        placesService.current.getDetails(
          {
            placeId: placeId,
            fields: ["formatted_address", "geometry"],
          },
          (place, status) => {
            if (status === google.maps.places.PlacesServiceStatus.OK && place) {
              const address = place.formatted_address || suggestion.description;
              const coordinates = place.geometry?.location
                ? {
                    lat: place.geometry.location.lat(),
                    lng: place.geometry.location.lng(),
                  }
                : suggestion.coordinates;

              onChange(address);
              onSelect(address, coordinates);
              setPredictions([]);
            }
          },
        );
      }
    },
    [onChange, onSelect],
  );

  // Handle keyboard navigation
  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (!showSuggestions || predictions.length === 0) return;

    switch (event.key) {
      case "ArrowDown":
        event.preventDefault();
        setSelectedIndex((prev) =>
          prev < predictions.length - 1 ? prev + 1 : 0,
        );
        break;
      case "ArrowUp":
        event.preventDefault();
        setSelectedIndex((prev) =>
          prev > 0 ? prev - 1 : predictions.length - 1,
        );
        break;
      case "Enter":
        event.preventDefault();
        if (selectedIndex >= 0 && selectedIndex < predictions.length) {
          handleSuggestionSelect(predictions[selectedIndex]);
        }
        break;
      case "Escape":
        setShowSuggestions(false);
        setSelectedIndex(-1);
        break;
    }
  };

  // Handle click outside to close suggestions
  useEffect(() => {
    if (!showSuggestions) return;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;

      // Check if click is outside both the input and suggestions
      const clickInsideInput =
        inputRef.current && inputRef.current.contains(target);
      const clickInsideSuggestions =
        suggestionsRef.current && suggestionsRef.current.contains(target);

      // Only close if click is outside both
      if (!clickInsideInput && !clickInsideSuggestions) {
        setShowSuggestions(false);
        setSelectedIndex(-1);
      }
    };

    // Use capture phase to catch events before they're stopped
    document.addEventListener("mousedown", handleClickOutside, true);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside, true);
    };
  }, [showSuggestions]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  // Debug: Log when suggestions change
  useEffect(() => {
    // console.log(
    //   "Suggestions updated:",
    //   predictions.length,
    //   "showSuggestions:",
    //   showSuggestions
    // );
  }, [predictions, showSuggestions]);

  return (
    <Box sx={{ position: "relative", ...sx }}>
      <TextField
        ref={inputRef}
        label={label}
        placeholder={placeholder}
        value={value}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        onFocus={() => {
          if (predictions.length > 0) {
            setShowSuggestions(true);
          }
        }}
        onBlur={(e) => {
          // Delay hiding suggestions to allow click events on suggestions to fire first
          // The click outside handler will handle closing if needed
          setTimeout(() => {
            // Only hide if we're not clicking on a suggestion
            const activeElement = document.activeElement;
            if (
              !suggestionsRef.current?.contains(activeElement) &&
              !inputRef.current?.contains(activeElement)
            ) {
              setShowSuggestions(false);
              setSelectedIndex(-1);
            }
          }, 200);
        }}
        disabled={disabled}
        fullWidth={fullWidth}
        size={size}
        inputProps={inputProps}
        InputProps={{
          endAdornment: loading ? (
            <CircularProgress
              size={16}
              sx={{
                opacity: 0.7,
                pointerEvents: "none",
              }}
            />
          ) : null,
        }}
      />

      {showSuggestions && predictions.length > 0 && (
        <Paper
          ref={suggestionsRef}
          sx={{
            position: "absolute",
            top: "100%",
            left: 0,
            right: 0,
            zIndex: 9999,
            maxHeight: 120,
            overflow: "auto",
            boxShadow: 3,
            border: 1,
            borderColor: "divider",
            borderRadius: 1,
            mt: 0.5,
            backgroundColor: "background.paper",
            "& .MuiListItemButton-root": {
              "&:hover": {
                backgroundColor: "action.hover",
              },
              "&.Mui-selected": {
                backgroundColor: "action.selected",
              },
            },
          }}
        >
          <List dense>
            {predictions.map((suggestion, index) => (
              <ListItem key={suggestion.id} disablePadding>
                <ListItemButton
                  onMouseDown={() => handleSuggestionSelect(suggestion)}
                  sx={{
                    backgroundColor:
                      index === selectedIndex
                        ? "action.selected"
                        : "transparent",
                    "&:hover": {
                      backgroundColor: "action.hover",
                    },
                    py: 0.5,
                    px: 1.5,
                    minHeight: 36,
                    borderBottom: "1px solid",
                    borderBottomColor: "divider",
                    "&:last-child": {
                      borderBottom: "none",
                    },
                  }}
                >
                  <ListItemText
                    primary={
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          flexWrap: "wrap",
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 500,
                            color: "text.primary",
                            lineHeight: 1.2,
                          }}
                        >
                          {suggestion.mainText}
                        </Typography>
                        {/* <Typography
                          variant="caption"
                          sx={{
                            color:
                              suggestion.source === "pelias"
                                ? "primary.main"
                                : "secondary.main",
                            fontSize: "0.7rem",
                            fontWeight: 600,

                            px: 0.5,
                            py: 0.25,
                            borderRadius: 0.5,
                            textTransform: "uppercase",
                          }}
                        >
                          {suggestion.source}
                        </Typography> */}
                      </Box>
                    }
                    secondary={
                      suggestion.secondaryText && (
                        <Typography
                          variant="caption"
                          sx={{
                            color: "text.secondary",
                            lineHeight: 1.2,
                            display: "block",
                            mt: 0.25,
                          }}
                        >
                          {suggestion.secondaryText}
                        </Typography>
                      )
                    }
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </Paper>
      )}
    </Box>
  );
};

export default GoogleMapsAutocomplete;
